import client from '@/api/client';
import { gql } from '@apollo/client';

import {
  AvatarData,
  UserRegistraionData,
  UserUpdateData,
} from '@/sagas/user';

const convertGqlRegMutations = (
  role: string,
  avatarData: AvatarData,
) => {
  let mutation = gql`
    mutation($userData: UserRegistraionInput) {
      ${role}Registration(userData: $userData) {
        status
      }
    }
  `;

  if (avatarData.file) {
    mutation = gql`
      mutation($userData: UserRegistraionInput, $file: Upload, $json: String) {
        ${role}Registration(userData: $userData) {
          status
        }
        saveAvatarFile(file: $file, json: $json) {
          status
        }
      }
    `;
  } else if (avatarData.json) {
    mutation = gql`
      mutation($userData: UserRegistraionInput, $json: String) {
        ${role}Registration(userData: $userData) {
          status
        }
        saveAvatarLink(json: $json) {
          status
        }
      }
    `;
  }

  return mutation;
};

const convertGqlUpdateMutations = (
  role: string,
  userData: UserUpdateData,
  avatarData: AvatarData,
) => {
  let mutation = gql`
    mutation($uid: Int, $userData: UserUpdateInput, $file: Upload, $json: String) {
      ${role}Update(uid: $uid, userData: $userData) {
        status
      }
      saveAvatarFile(file: $file, json: $json) {
        status
      }
    }
  `;

  if ((userData.name !== undefined || userData.skype !== undefined) && !avatarData.file) {
    mutation = gql`
      mutation($uid: Int, $userData: UserUpdateInput) {
        ${role}Update(uid: $uid, userData: $userData) {
          status
        }
      }
    `;
  } else if ((!userData.name === undefined && !userData.skype === undefined) && avatarData.file) {
    mutation = gql`
      mutation($file: Upload, $json: String) {
        saveAvatarFile(file: $file, json: $json) {
          status
        }
      }
    `;
  } else if ((!userData.name === undefined && !userData.skype === undefined) && !avatarData.file) {
    console.error('cant create gql, nothing update');
  }

  return mutation;
};

const registration = (
  role: string,
  userData: UserRegistraionData,
  avatarData: AvatarData,
) => client
  .mutate({
    mutation: convertGqlRegMutations(role, avatarData),
    variables: {
      userData,
      ...avatarData,
    },
  });

const update = (
  uid: number, // student id // teacher id
  role: string,
  userData: UserUpdateData,
  avatarData: AvatarData,
) => client
  .mutate({
    mutation: convertGqlUpdateMutations(role, userData, avatarData),
    variables: {
      uid,
      userData,
      ...avatarData,
    },
  });

const updatePassword = (
  password: string,
  userId: number,
) => client
  .mutate({
    mutation: gql`
      mutation (
        $password: String,
        $userId: Int
      ) {
        updatePassword(
          password: $password,
          userId: $userId
        ) {
          status
        }
      }
    `,
    variables: {
      password,
      userId,
    },
  });

const sendFeedback = (
  email: string,
  message: string,
  role: string,
  uid: number,
  module: string,
  screenshot?: File,
) => client
  .mutate({
    mutation: gql`
      mutation (
        $email: String,
        $message: String,
        $role: String,
        $uid: Int,
        $module: String,
        $screenshot: Upload,
      ) {
        sendFeedback(
          email: $email,
          message: $message,
          role: $role,
          uid: $uid,
          module: $module,
          screenshot: $screenshot
        ) {
          status
        }
      }
    `,
    variables: {
      email,
      message,
      role,
      uid,
      module,
      screenshot,
    },
  });

const getUsers = (email: string) => client
  .query({
    query: gql`
      query($email: String) {
        getUsers(email: $email) {
          id
        }
      }
    `,
    variables: {
      email,
    },
  });

const getAuthLink = (data: { id: number; hash: string; }) => client
  .mutate({
    mutation: gql`
      mutation(
        $id: Int,
        $hash: String
      ) {
        getAuthLink(
          id: $id,
          hash: $hash,
        ) {
          hash
        }
      }
    `,
    variables: {
      ...data,
    },
  });

export default {
  getAuthLink,
  getUsers,
  registration,
  update,
  updatePassword,
  sendFeedback,
};
